import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import styles from './controls.module.css'

type Variant = 'default' | 'primary' | 'danger' | 'quiet'

export function Button({
  variant = 'default',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  const variantClass = variant === 'default' ? '' : styles[variant]
  return <button type="button" {...props} className={`${styles.button} ${variantClass} ${className ?? ''}`} />
}

export function IconButton({
  danger,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { danger?: boolean }) {
  return (
    <button
      type="button"
      {...props}
      className={`${styles.icon} ${danger ? styles.iconDanger : ''} ${className ?? ''}`}
    />
  )
}

interface FieldProps {
  label: string
  hint?: ReactNode
  error?: string | null
  children: (id: string) => ReactNode
}

export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId()
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      {children(id)}
      {hint ? <span className={styles.hint}>{hint}</span> : null}
      {error ? (
        <span className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  )
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${styles.input} ${props.className ?? ''}`} />
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${styles.select} ${props.className ?? ''}`} />
}

export interface SwatchOption {
  value: string
  label: string
  /** Null draws the "no colour" swatch. */
  colour: string | null
}

/** The colour chooser shared by the card editor and the mindmap: one swatch per colour. */
export function ColourSwatches({
  id,
  label,
  options,
  value,
  onPick,
}: {
  id?: string
  label: string
  options: readonly SwatchOption[]
  value: string | null
  onPick: (value: string) => void
}) {
  return (
    <div id={id} className={styles.swatches} role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          aria-label={option.label}
          title={option.label}
          className={`${styles.swatch} ${option.colour ? '' : styles.swatchNone}`}
          style={option.colour ? { background: option.colour } : undefined}
          onClick={() => onPick(option.value)}
        />
      ))}
    </div>
  )
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${styles.textarea} ${props.className ?? ''}`} />
}

interface InlineTitleProps {
  value: string
  onCommit: (value: string) => void
  /** Accessible name for the edit affordance, e.g. "Rename column Browse". */
  label: string
  maxLength?: number
  /** Open straight into the input, for a title created specifically to be renamed. */
  autoEdit?: boolean
  /** Called when editing ends, however it ended, so a caller can drop its focus request. */
  onEditEnd?: () => void
}

/**
 * Click or press Enter to edit in place. Enter and blur commit, Escape cancels.
 * The draft never touches board state, so typing here cannot rerender the map.
 */
export function InlineTitle({
  value,
  onCommit,
  label,
  maxLength = 200,
  autoEdit = false,
  onEditEnd,
}: InlineTitleProps) {
  const [editing, setEditing] = useState(autoEdit)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  const cancelled = useRef(false)

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  if (!editing) {
    return (
      <button
        type="button"
        className={styles.inlineTitle}
        title={value}
        aria-label={label}
        onClick={() => {
          cancelled.current = false
          setDraft(value)
          setEditing(true)
        }}
      >
        {value}
      </button>
    )
  }

  const commit = () => {
    setEditing(false)
    onEditEnd?.()
    if (cancelled.current) return
    const trimmed = draft.trim()
    if (trimmed.length > 0 && trimmed !== value) onCommit(trimmed)
  }

  return (
    <input
      ref={inputRef}
      className={styles.inlineInput}
      value={draft}
      aria-label={label}
      maxLength={maxLength}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          commit()
        } else if (event.key === 'Escape') {
          event.preventDefault()
          cancelled.current = true
          setDraft(value)
          setEditing(false)
          onEditEnd?.()
        }
      }}
    />
  )
}
